import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiUsers, 
  FiClock, 
  FiCheckCircle, 
  FiTrendingUp,
  FiArrowRight,
  FiCalendar
} from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import api from '../../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, applicationsRes] = await Promise.all([
        api.get('/applications/stats'),
        api.get('/applications?limit=5')
      ]);
      setStats(statsRes.data);
      setRecentApplications(applicationsRes.data.applications);
    } catch (error) {
      console.error('Veriler yüklenemedi:', error);
      // Demo data
      setStats({
        total: 156,
        byStatus: { pending: 42, approved: 78, rejected: 12, contacted: 18, registered: 6 },
        byExamType: [
          { _id: 'LGS', count: 89 },
          { _id: 'YKS', count: 52 },
          { _id: 'Genel', count: 15 }
        ],
        last7Days: 23,
        last30Days: 67
      });
      setRecentApplications([
        { _id: '1', studentName: 'Ahmet', studentSurname: 'Yılmaz', examType: 'LGS', status: 'pending', createdAt: new Date() },
        { _id: '2', studentName: 'Ayşe', studentSurname: 'Kaya', examType: 'YKS', status: 'approved', createdAt: new Date() },
        { _id: '3', studentName: 'Mehmet', studentSurname: 'Demir', examType: 'LGS', status: 'contacted', createdAt: new Date() }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      label: 'Toplam Başvuru', 
      value: stats?.total || 0, 
      icon: FiUsers, 
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
    },
    { 
      label: 'Bekleyen', 
      value: stats?.byStatus?.pending || 0, 
      icon: FiClock, 
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    },
    { 
      label: 'Onaylanan', 
      value: stats?.byStatus?.approved || 0, 
      icon: FiCheckCircle, 
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    },
    { 
      label: 'Son 7 Gün', 
      value: stats?.last7Days || 0, 
      icon: FiTrendingUp, 
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
    }
  ];

  const pieColors = ['#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6'];
  const pieData = stats?.byStatus ? [
    { name: 'Bekleyen', value: stats.byStatus.pending },
    { name: 'Onaylanan', value: stats.byStatus.approved },
    { name: 'Reddedilen', value: stats.byStatus.rejected },
    { name: 'İletişime Geçildi', value: stats.byStatus.contacted },
    { name: 'Kayıt Yapıldı', value: stats.byStatus.registered }
  ] : [];

  const barData = stats?.byExamType?.map(item => ({
    name: item._id,
    value: item.count
  })) || [];

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Bekliyor', class: 'badge-pending' },
      approved: { label: 'Onaylandı', class: 'badge-approved' },
      rejected: { label: 'Reddedildi', class: 'badge-rejected' },
      contacted: { label: 'İletişime Geçildi', class: 'badge-contacted' },
      registered: { label: 'Kayıt Yapıldı', class: 'badge-registered' }
    };
    const info = statusMap[status] || { label: status, class: '' };
    return <span className={`badge ${info.class}`}>{info.label}</span>;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Stats Cards */}
      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="stat-icon" style={{ background: stat.gradient }}>
              <stat.icon />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="charts-grid">
        <motion.div 
          className="chart-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3>Başvuru Durumları</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="chart-legend">
              {pieData.map((item, index) => (
                <div key={index} className="legend-item">
                  <span className="legend-color" style={{ background: pieColors[index] }}></span>
                  <span className="legend-label">{item.name}</span>
                  <span className="legend-value">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="chart-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3>Sınav Türüne Göre</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Recent Applications */}
      <motion.div 
        className="recent-applications"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="section-header">
          <h3>Son Başvurular</h3>
          <Link to="/admin/basvurular" className="view-all">
            Tümünü Gör <FiArrowRight />
          </Link>
        </div>
        <div className="applications-table">
          <table>
            <thead>
              <tr>
                <th>Öğrenci</th>
                <th>Sınav Türü</th>
                <th>Durum</th>
                <th>Tarih</th>
              </tr>
            </thead>
            <tbody>
              {recentApplications.map((app) => (
                <tr key={app._id}>
                  <td>
                    <div className="student-info">
                      <span className="student-name">{app.studentName} {app.studentSurname}</span>
                    </div>
                  </td>
                  <td>{app.examType}</td>
                  <td>{getStatusBadge(app.status)}</td>
                  <td>
                    <div className="date-info">
                      <FiCalendar />
                      {formatDate(app.createdAt)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;

